import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase, handleSupabaseError, logger, RuleRejectionError } from '@/lib';
import { getCurrentAdminDetails } from '@/lib/admin-details';
import { logActivity } from '@/utils';
import { useInvalidateMemberProfile } from './useInvalidateMemberProfile';
import type { MembershipFreeze } from '@/types';
import { MEMBERSHIP_QUERY_KEYS } from '@/constants';

interface CreateFreezeInput {
  membershipId: string;
  memberId: string;
  startDate: string;
  endDate: string;
  fee: number;
  reason: string;
}

interface UpdateFreezeInput {
  freezeId: string;
  membershipId: string;
  memberId: string;
  startDate: string;
  endDate: string;
  fee: number;
  reason: string;
  /** "End freeze today": logged as an update with `ended_early` metadata. */
  endedEarly?: boolean;
}

interface DeleteFreezeInput {
  freezeId: string;
  membershipId: string;
  memberId: string;
}

async function getCurrentAdmin() {
  const admin = await getCurrentAdminDetails(supabase);
  return {
    adminId: admin.id,
    adminName: admin.display_name,
    adminEmail: admin.email,
  };
}

interface FreezeMutationResult {
  freeze: MembershipFreeze | null;
  old_freeze: MembershipFreeze | null;
  new_membership_end_date: string;
}

function normalizeFreezeReason(reason: string | null | undefined): string {
  const normalized = reason?.trim() ?? '';
  if (!normalized) throw new Error('Reason is required');
  return normalized;
}

// The database locks the membership and applies only the change in freeze days
// to its current end_date. No fallback to the old multi-write recalculation:
// it could overwrite a manual adjustment or save only half of the operation.
async function mutateFreeze(
  action: 'create' | 'update' | 'delete',
  input: CreateFreezeInput | UpdateFreezeInput | DeleteFreezeInput,
): Promise<FreezeMutationResult> {
  const { data, error } = await supabase.rpc('mutate_membership_freeze', {
    p_action: action,
    p_membership_id: input.membershipId,
    p_member_id: input.memberId,
    p_freeze_id: 'freezeId' in input ? input.freezeId : null,
    ...('startDate' in input ? {
      p_start_date: input.startDate,
      p_end_date: input.endDate,
      p_fee: input.fee,
      p_reason: input.reason,
    } : {}),
  });
  if (error) {
    if (error.code === 'PGRST202') {
      throw new Error('Freeze updates are temporarily unavailable. Please contact your administrator.');
    }
    // A freeze rule the admin broke (dates outside the membership, an
    // overlap), worded for the toast.
    if (error.code === '22023') throw new RuleRejectionError(error.message);
    handleSupabaseError(error, { operation: `${action} membership freeze` });
  }
  if (!data) throw new Error('Missing freeze update result');
  return data as FreezeMutationResult;
}

// Both boundary dates are frozen days: end_date is the LAST frozen day, and the
// member regains access the day after. Hence the +1 (a same-day freeze is 1 day).
function calculateDuration(startDate: string, endDate: string): number {
  const start = new Date(startDate + 'T00:00:00');
  const end = new Date(endDate + 'T00:00:00');
  return Math.round((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)) + 1;
}

/**
 * Fetch all freezes for a membership
 */
export function useMembershipFreezes(membershipId: string) {
  return useQuery({
    queryKey: MEMBERSHIP_QUERY_KEYS.FREEZES(membershipId),
    enabled: Boolean(membershipId),
    queryFn: async () => {
      if (!membershipId) return [] as MembershipFreeze[];

      const { data, error } = await supabase
        .from('membership_freezes')
        .select('*')
        .eq('membership_id', membershipId)
        .order('start_date', { ascending: true });

      if (error) handleSupabaseError(error, { operation: 'fetching membership freezes' });
      return (data || []) as MembershipFreeze[];
    },
  });
}

/** A future booking a freeze over the given days would cancel. */
export interface FreezeBookingImpact {
  id: string;
  kind: 'class' | 'waitlist' | 'booth';
  title: string;
  startsAt: string;
  /** frozen: a day the freeze covers; after_end: past the membership's new end. */
  reason: 'frozen' | 'after_end';
}

export type FreezeChange =
  | { action: 'create'; startDate: string; endDate: string }
  | { action: 'update'; freezeId: string; startDate: string; endDate: string }
  | { action: 'delete'; freezeId: string };

/**
 * What saving a freeze change will cancel, asked of the database:
 * preview_membership_freeze_change (hok-os 20261103130000) runs the same
 * mutate_membership_freeze in a subtransaction it rolls back, and lists the
 * bookings, waitlist places and call booths the revoke triggers would take —
 * on newly frozen days, and past the membership's end when the change moves
 * it earlier (ending early, shortening, deleting). Bookings another
 * membership still covers are not listed. A change the freeze rules refuse
 * previews as nothing; the dialog shows its own validation message.
 */
export function useFreezeBookingImpact(
  memberId: string,
  membershipId: string,
  change: FreezeChange | null,
  enabled: boolean,
) {
  const datesValid = !change || change.action === 'delete'
    || Boolean(change.startDate && change.endDate && change.startDate <= change.endDate);
  return useQuery({
    queryKey: MEMBERSHIP_QUERY_KEYS.FREEZE_IMPACT(memberId, membershipId, change),
    enabled: enabled && Boolean(memberId && membershipId && change) && datesValid,
    staleTime: 15 * 1000,
    queryFn: async (): Promise<FreezeBookingImpact[]> => {
      if (!change) return [];
      const { data, error } = await supabase.rpc('preview_membership_freeze_change', {
        p_action: change.action,
        p_membership_id: membershipId,
        p_member_id: memberId,
        p_freeze_id: change.action === 'create' ? null : change.freezeId,
        p_start_date: change.action === 'delete' ? null : change.startDate,
        p_end_date: change.action === 'delete' ? null : change.endDate,
      });
      if (error) handleSupabaseError(error, { operation: 'checking bookings a freeze change cancels' });

      type Row = { id: string; kind: FreezeBookingImpact['kind']; title: string | null; starts_at: string; reason: FreezeBookingImpact['reason'] };
      const rows = ((data as { items?: Row[] } | null)?.items ?? []);
      return rows.map(r => ({
        id: r.id,
        kind: r.kind,
        title: r.title ?? (r.kind === 'booth' ? 'Call booth' : 'Class'),
        startsAt: r.starts_at,
        reason: r.reason,
      }));
    },
  });
}

/**
 * Fetch total freeze days per membership (for membership list summary)
 */
export function useBulkMembershipFreezeSummary(membershipIds: string[]) {
  return useQuery({
    queryKey: MEMBERSHIP_QUERY_KEYS.FREEZE_SUMMARY(membershipIds),
    enabled: membershipIds.length > 0,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('membership_freezes')
        .select('membership_id, duration_days')
        .in('membership_id', membershipIds);

      if (error) handleSupabaseError(error, { operation: 'fetching freeze summaries' });

      // Aggregate: { membershipId: totalDays }
      const summary: Record<string, number> = {};
      for (const f of data || []) {
        summary[f.membership_id] = (summary[f.membership_id] || 0) + f.duration_days;
      }
      return summary;
    },
    // Tab-click on the Memberships tab remounts this hook;
    // refetch each time so the row badges show fresh freeze totals.
    refetchOnMount: 'always',
  });
}

/**
 * Create a new freeze
 */
export function useCreateMembershipFreeze() {
  const invalidateMemberProfile = useInvalidateMemberProfile();

  return useMutation({
    mutationFn: async (input: CreateFreezeInput) => {
      const reason = normalizeFreezeReason(input.reason);
      const admin = await getCurrentAdmin();
      const durationDays = calculateDuration(input.startDate, input.endDate);

      if (durationDays <= 0) throw new Error('End date cannot be before start date');

      const normalizedInput = { ...input, reason };
      const { freeze: data, new_membership_end_date: newEndDate } = await mutateFreeze('create', normalizedInput);
      if (!data) throw new Error('Missing created freeze');

      // Log activity
      logActivity(
        'membership_freeze_added',
        'membership',
        input.membershipId,
        {},
        {
          freeze_id: data.id,
          start_date: input.startDate,
          end_date: input.endDate,
          duration_days: durationDays,
          fee: input.fee,
          reason,
          new_membership_end_date: newEndDate,
          admin_name: admin.adminName,
          admin_email: admin.adminEmail,
        },
        input.memberId
      ).catch(() => {});

      // Fire-and-forget: generate freeze receipt in background
      supabase.auth.getSession().then(({ data: { session } }) => {
        if (!session?.access_token) return;
        fetch(`/api/generate-freeze-receipt?id=${data.id}`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session.access_token}`,
            'Content-Type': 'application/json'
          }
        })
          .then(async (response) => {
            if (response.ok) {
              logger.info('Background freeze receipt generation successful', { freezeId: data.id }, 'useMembershipFreezes');
            } else {
              const errorData = await response.json().catch(() => ({ error: 'Invalid response' }));
              logger.error('Background freeze receipt generation failed:', errorData);
            }
          })
          .catch(err => {
            logger.error('Background freeze receipt generation error:', err);
          });
      });

      return data as MembershipFreeze;
    },
    // The member profile covers the whole membership family, including this
    // membership's freezes, logs and every freeze summary.
    onSuccess: (_data, variables) => {
      invalidateMemberProfile(variables.memberId);
    },
  });
}

/**
 * Update an existing freeze
 */
export function useUpdateMembershipFreeze() {
  const invalidateMemberProfile = useInvalidateMemberProfile();

  return useMutation({
    mutationFn: async (input: UpdateFreezeInput) => {
      const reason = normalizeFreezeReason(input.reason);
      const admin = await getCurrentAdmin();
      const durationDays = calculateDuration(input.startDate, input.endDate);

      if (durationDays <= 0) throw new Error('End date cannot be before start date');

      const {
        freeze: data, old_freeze: oldFreeze, new_membership_end_date: newEndDate,
      } = await mutateFreeze('update', { ...input, reason });
      if (!data || !oldFreeze) throw new Error('Missing updated freeze');

      logActivity(
        'membership_freeze_updated',
        'membership',
        input.membershipId,
        {
          start_date: { from: oldFreeze?.start_date ?? null, to: input.startDate },
          end_date: { from: oldFreeze?.end_date ?? null, to: input.endDate },
          duration_days: { from: oldFreeze?.duration_days ?? null, to: durationDays },
          fee: { from: oldFreeze?.fee ?? null, to: input.fee },
          reason: { from: oldFreeze?.reason ?? null, to: reason },
        },
        {
          freeze_id: input.freezeId,
          ...(input.endedEarly ? { ended_early: true } : {}),
          new_membership_end_date: newEndDate,
          admin_name: admin.adminName,
          admin_email: admin.adminEmail,
        },
        input.memberId
      ).catch(() => {});

      // Fire-and-forget: regenerate receipt with updated data
      supabase.auth.getSession().then(({ data: { session } }) => {
        if (!session?.access_token) return;
        fetch(`/api/generate-freeze-receipt?id=${input.freezeId}`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session.access_token}`,
            'Content-Type': 'application/json',
          },
        }).catch(() => {});
      });

      return data as MembershipFreeze;
    },
    // The member profile covers the whole membership family, including this
    // membership's freezes, logs and every freeze summary.
    onSuccess: (_data, variables) => {
      invalidateMemberProfile(variables.memberId);
    },
  });
}

/**
 * Delete a freeze
 */
export function useDeleteMembershipFreeze() {
  const invalidateMemberProfile = useInvalidateMemberProfile();

  return useMutation({
    mutationFn: async (input: DeleteFreezeInput) => {
      const admin = await getCurrentAdmin();

      const {
        old_freeze: freeze, new_membership_end_date: newEndDate,
      } = await mutateFreeze('delete', input);

      if (freeze) {
        logActivity(
          'membership_freeze_deleted',
          'membership',
          input.membershipId,
          {
            start_date: { from: freeze.start_date, to: null },
            end_date: { from: freeze.end_date, to: null },
            duration_days: { from: freeze.duration_days, to: null },
          },
          {
            freeze_id: input.freezeId,
            deleted_freeze: {
              start_date: freeze.start_date,
              end_date: freeze.end_date,
              duration_days: freeze.duration_days,
              fee: freeze.fee,
              reason: freeze.reason,
            },
            new_membership_end_date: newEndDate,
            admin_name: admin.adminName,
            admin_email: admin.adminEmail,
          },
          input.memberId
        ).catch(() => {});
      }
    },
    // The member profile covers the whole membership family, including this
    // membership's freezes, logs and every freeze summary.
    onSuccess: (_data, variables) => {
      invalidateMemberProfile(variables.memberId);
    },
  });
}

/**
 * Generate a freeze receipt (or regenerate)
 */
export function useGenerateFreezeReceipt() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ freezeId }: { freezeId: string; membershipId: string }) => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) throw new Error('You must be logged in.');

      const response = await fetch(`/api/generate-freeze-receipt?id=${freezeId}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Failed to generate receipt' }));
        throw new Error(errorData.error || 'Failed to generate receipt');
      }

      return response.json();
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: MEMBERSHIP_QUERY_KEYS.FREEZES(variables.membershipId) });
    },
  });
}

/**
 * Download a freeze receipt (get signed URL and open in new tab)
 */
export function useFreezeReceiptDownload() {
  return useMutation({
    mutationFn: async ({ freezeId }: { freezeId: string }) => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) throw new Error('You must be logged in.');

      const response = await fetch(`/api/freeze-receipt-download?id=${freezeId}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
        }
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Failed to download receipt' }));
        throw new Error(errorData.error || 'Failed to download receipt');
      }

      const data = await response.json();
      window.open(data.downloadUrl, '_blank');
      return data;
    },
  });
}
